import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Paper, Divider, List, ListItem, ListItemText, Chip, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { listQuizzes, Quiz, getAttemptResults, listQuizAttempts } from '../services/quizService';
import { QuizTakingDialog } from './Quizzes';
import { getStudentByUserId } from '../services/studentService';
import QuizIcon from '@mui/icons-material/Quiz';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// TODO: import attendance and grades services

// Mascot SVG (Owl, placeholder)
const MascotOwl = () => (
  <svg width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="36" cy="48" rx="24" ry="18" fill="#1E3A8A" />
    <ellipse cx="26" cy="48" rx="7" ry="9" fill="#fff" />
    <ellipse cx="46" cy="48" rx="7" ry="9" fill="#fff" />
    <ellipse cx="26" cy="48" rx="3" ry="4" fill="#F97316" />
    <ellipse cx="46" cy="48" rx="3" ry="4" fill="#F97316" />
    <ellipse cx="26" cy="48" rx="1.5" ry="2" fill="#222" />
    <ellipse cx="46" cy="48" rx="1.5" ry="2" fill="#222" />
    <polygon points="36,54 32,60 40,60" fill="#F97316" />
    <ellipse cx="36" cy="38" rx="12" ry="8" fill="#1E3A8A" />
    <rect x="32" y="30" width="8" height="4" rx="2" fill="#fff" />
    <rect x="30" y="26" width="12" height="6" rx="3" fill="#1E3A8A" />
    <ellipse cx="36" cy="24" rx="16" ry="8" fill="#1E3A8A" />
    <rect x="32" y="16" width="8" height="8" rx="4" fill="#F97316" />
    <rect x="34" y="10" width="4" height="8" rx="2" fill="#1E3A8A" />
  </svg>
);

// Add a helper function to render errors safely
function renderError(error: any) {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error.detail) {
    if (Array.isArray(error.detail)) {
      return error.detail.map((e: any, i: number) => <div key={i}>{e.msg || JSON.stringify(e)}</div>);
    }
    return error.detail.toString();
  }
  return JSON.stringify(error);
}

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  const { showError } = useFeedback();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [quizAttempts, setQuizAttempts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [takingQuizId, setTakingQuizId] = useState<number | null>(null);
  const [student, setStudent] = useState<any>(null);
  // TODO: attendance, grades state

  useEffect(() => {
    const fetchStudentAndQuizzes = async () => {
      setLoading(true);
      try {
        if (user?.id) {
          const s = await getStudentByUserId(user.id);
          setStudent(s);
          const allQuizzes = await listQuizzes();
          setQuizzes(allQuizzes.filter(q => q.class_level === s?.class_level));
          setQuizAttempts(await listQuizAttempts(user?.id) as any[]);
        }
        // TODO: fetch attendance, grades
      } catch (e: any) {
        showError(e?.response?.data?.detail || e?.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchStudentAndQuizzes();
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [user, showError]);

  // Split quizzes
  const liveQuizzes = quizzes.filter(q => q.mode === 'live' && q.start_time && q.end_time);
  const upcomingQuizzes = quizzes.filter(q => q.mode !== 'live' && dayjs(q.start_time).isAfter(now));
  const completedQuizIds = new Set(quizAttempts.map((a: any) => a.quiz_id));
  const completedQuizzes = quizzes.filter(q => completedQuizIds.has(q.id));

  const getCountdown = (start: string, end: string) => {
    const startMs = dayjs(start).valueOf();
    const endMs = dayjs(end).valueOf();
    if (now < startMs) {
      const diff = startMs - now;
      return { status: 'upcoming', diff };
    } else if (now >= startMs && now <= endMs) {
      const diff = endMs - now;
      return { status: 'open', diff };
    } else {
      return { status: 'ended', diff: 0 };
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Grid container spacing={3}>
        {/* Welcome Banner */}
        <Grid size={12}>
          <Paper sx={{ display: 'flex', alignItems: 'center', p: { xs: 2, md: 3 }, bgcolor: '#F9FAFB', borderRadius: 3, boxShadow: 2 }}>
            <Box sx={{ mr: 3, display: { xs: 'none', sm: 'block' } }}>
              <MascotOwl />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E3A8A' }} gutterBottom>
                Welcome, {user?.full_name || user?.username}!
              </Typography>
              <Typography variant="subtitle1" sx={{ color: '#6B7280' }}>
                {student?.class_level ? `Class: ${student.class_level}` : ''}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: '#F97316', fontWeight: 500 }}>
                Ready to learn and achieve great things today?
              </Typography>
            </Box>
            <Box sx={{ ml: 3, display: { xs: 'none', md: 'block' } }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: '#1E3A8A', fontSize: 36 }}>
                {user?.full_name ? user.full_name[0] : user?.username?.[0] || 'S'}
              </Avatar>
            </Box>
          </Paper>
        </Grid>
        {/* Announcements Section */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 1 }}>Announcements</Typography>
            {/* TODO: Fetch and display announcements/messages */}
            <Typography color="text.secondary">No announcements at this time.</Typography>
          </Paper>
        </Grid>
        {/* Placeholder for next sections (Quizzes, Attendance, Grades) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, minHeight: 120, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: '#F97316', color: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Your learning journey starts here!</Typography>
          </Paper>
        </Grid>
        {/* Quizzes Section */}
        <Grid size={12}>
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#1E3A8A', fontWeight: 700 }}>Quizzes</Typography>
          <Grid container spacing={2}>
            {/* Live Quizzes */}
            {liveQuizzes.length > 0 && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper sx={{ p: 2, bgcolor: '#E0E7FF', borderRadius: 3, minHeight: 220 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <QuizIcon sx={{ color: '#1E3A8A', mr: 1 }} />
                    <Typography variant="h6" sx={{ color: '#1E3A8A' }}>Live Quizzes</Typography>
                  </Box>
                  {loading ? <CircularProgress /> : (
                    liveQuizzes.length === 0 ? <Typography color="text.secondary">No live quizzes.</Typography> :
                      liveQuizzes.map(quiz => {
                        const countdown = getCountdown(quiz.start_time!, quiz.end_time!);
                        let countdownText = '';
                        if (countdown.status === 'upcoming') {
                          const mins = Math.floor(countdown.diff / 60000);
                          const secs = Math.floor((countdown.diff % 60000) / 1000);
                          countdownText = `Opens in ${mins}m ${secs}s`;
                        } else if (countdown.status === 'open') {
                          const mins = Math.floor(countdown.diff / 60000);
                          const secs = Math.floor((countdown.diff % 60000) / 1000);
                          countdownText = `Closes in ${mins}m ${secs}s`;
                        } else {
                          countdownText = 'Closed';
                        }
                        return (
                          <Paper key={quiz.id} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography variant="subtitle1" fontWeight={600}>{quiz.title}</Typography>
                                <Typography variant="body2" color="text.secondary">{quiz.class_level && `[Class: ${quiz.class_level}]`}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {dayjs(quiz.start_time).format('YYYY-MM-DD HH:mm')} - {dayjs(quiz.end_time).format('HH:mm')}
                                </Typography>
                                <Chip label={countdownText} color={countdown.status === 'open' ? 'success' : countdown.status === 'upcoming' ? 'info' : 'default'} size="small" sx={{ mt: 0.5 }} />
                              </Box>
                              <Box>
                                {countdown.status === 'open' ? (
                                  <Button variant="contained" color="primary" size="small" onClick={() => setTakingQuizId(quiz.id)}>Join</Button>
                                ) : (
                                  <Button variant="outlined" size="small" disabled>{countdown.status === 'upcoming' ? 'Not Open' : 'Closed'}</Button>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })
                  )}
                </Paper>
              </Grid>
            )}
            {/* Upcoming Quizzes */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: '#FEF3C7', borderRadius: 3, minHeight: 220 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EventAvailableIcon sx={{ color: '#F97316', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#F97316' }}>Upcoming</Typography>
                </Box>
                {loading ? <CircularProgress /> : (
                  upcomingQuizzes.length === 0 ? <Typography color="text.secondary">No upcoming quizzes.</Typography> :
                    upcomingQuizzes.map(quiz => (
                      <Paper key={quiz.id} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{quiz.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{quiz.class_level && `[Class: ${quiz.class_level}]`}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {quiz.start_time && dayjs(quiz.start_time).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                        <Chip label="Upcoming" color="info" size="small" sx={{ mt: 0.5 }} />
                      </Paper>
                    ))
                )}
              </Paper>
            </Grid>
            {/* Completed Quizzes */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: '#DCFCE7', borderRadius: 3, minHeight: 220 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CheckCircleIcon sx={{ color: '#22C55E', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#22C55E' }}>Completed</Typography>
                </Box>
                {loading ? <CircularProgress /> : (
                  completedQuizzes.length === 0 ? <Typography color="text.secondary">No completed quizzes yet.</Typography> :
                    completedQuizzes.map(quiz => (
                      <Paper key={quiz.id} sx={{ p: 1.5, mb: 1.5, bgcolor: '#fff', borderRadius: 2, boxShadow: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>{quiz.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{quiz.class_level && `[Class: ${quiz.class_level}]`}</Typography>
                        {/* TODO: Show score/result */}
                        <Chip label="Completed" color="success" size="small" sx={{ mt: 0.5 }} />
                      </Paper>
                    ))
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        {/* Attendance and Grades sections will follow in next steps */}
      </Grid>
      {/* Quiz Taking Dialog */}
      {takingQuizId && (
        <QuizTakingDialog quizId={takingQuizId} onClose={() => setTakingQuizId(null)} />
      )}
    </Box>
  );
};

export default StudentDashboard; 